import { useEffect, useState } from 'react';
import { BrowserRouter, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import {
  ApiError,
  bootstrapOwner,
  clearAdminAuth,
  getAdminIdentity,
  getAdminKey,
  getAdminToken,
  getBootstrapNeeded,
  getNavBadges,
  getUsers,
  login,
  mediaUrl,
  setAdminKey,
  setAdminSession,
  UNAUTHORIZED_EVENT,
  type AdminIdentity,
  type AdminUser,
  type NavBadges,
} from './api';
import { Admins } from './pages/Admins';
import { Ai } from './pages/Ai';
import { Broadcast } from './pages/Broadcast';
import { Catalog } from './pages/Catalog';
import { Dashboard } from './pages/Dashboard';
import { Moderation } from './pages/Moderation';
import { Payments } from './pages/Payments';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { Translations } from './pages/Translations';
import { Users } from './pages/Users';
import { UserDetail } from './pages/UserDetail';
import { PaymentSettings } from './pages/PaymentSettings';
import { Importer } from './pages/Importer';
import { TwoFactorPanel } from './TwoFactorPanel';
import { Icon, type IconName } from './components/Icon';
import { useTheme } from './theme';

/**
 * Admin auth realm (docs/09): email+password login with TOTP 2FA, RBAC roles
 * and an audit log. The legacy X-Admin-Key remains available as a fallback
 * ("Use legacy admin key") and for first-run owner bootstrap.
 */

type Session = { kind: 'jwt'; admin: AdminIdentity } | { kind: 'legacy' };

function initialSession(): Session | null {
  const admin = getAdminIdentity();
  if (getAdminToken() && admin) return { kind: 'jwt', admin };
  if (getAdminKey()) return { kind: 'legacy' };
  return null;
}

/** First-run setup: needs the legacy key to mint the single owner account. */
function BootstrapForm({ onAuthed }: { onAuthed: (s: Session) => void }) {
  const [key, setKey] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await bootstrapOwner(key.trim(), email.trim(), name.trim(), password);
      setAdminSession(res.token, res.admin);
      onAuthed({ kind: 'jwt', admin: res.admin });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="gate-card" onSubmit={submit}>
      <h1>
        First-run setup<span className="dot">.</span>
      </h1>
      <p className="gate-foot">
        No admin accounts exist yet. Enter the legacy admin key to create the owner account.
      </p>
      <input
        className="input"
        type="password"
        placeholder="Legacy admin key"
        autoFocus
        value={key}
        onChange={(e) => setKey(e.target.value)}
      />
      <input
        className="input"
        type="email"
        placeholder="Owner email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        className="input"
        type="text"
        placeholder="Owner name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="input"
        type="password"
        placeholder="Password (min 8 chars)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      {error && <p className="error-line">{error}</p>}
      <button
        type="submit"
        className="gate-submit"
        disabled={busy || !key.trim() || !email.trim() || !name.trim() || password.length < 8}
      >
        {busy ? 'Creating…' : 'Create owner account'}
      </button>
    </form>
  );
}

function LoginForm({ onAuthed }: { onAuthed: (s: Session) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [totpNeeded, setTotpNeeded] = useState(false);
  const [legacyMode, setLegacyMode] = useState(false);
  const [legacyKey, setLegacyKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await login(email.trim(), password, totpNeeded ? totp.trim() : undefined);
      setAdminSession(res.token, res.admin);
      onAuthed({ kind: 'jwt', admin: res.admin });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'admin/totp_required') {
        setTotpNeeded(true);
        setError(null);
      } else if (err instanceof ApiError && err.code === 'admin/totp_invalid') {
        setTotpNeeded(true);
        setError('Invalid TOTP code — try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Login failed.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (legacyMode) {
    return (
      <form
        className="gate-card"
        onSubmit={(e) => {
          e.preventDefault();
          const key = legacyKey.trim();
          if (!key) return;
          setAdminKey(key);
          onAuthed({ kind: 'legacy' });
        }}
      >
        <h1>
          FLM Admin<span className="dot">.</span>
        </h1>
        <input
          className="input"
          type="password"
          placeholder="Legacy admin key"
          autoFocus
          value={legacyKey}
          onChange={(e) => setLegacyKey(e.target.value)}
        />
        <button type="submit" className="gate-submit">
          Enter with key
        </button>
        <button type="button" className="link-btn" onClick={() => setLegacyMode(false)}>
          ← Back to account login
        </button>
        <p className="gate-foot">
          The legacy X-Admin-Key acts as a full owner session. Prefer a personal admin account with
          2FA.
        </p>
      </form>
    );
  }

  return (
    <form className="gate-card" onSubmit={submit}>
      <h1>
        FLM Admin<span className="dot">.</span>
      </h1>
      <input
        className="input"
        type="email"
        placeholder="Email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        className="input"
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      {totpNeeded && (
        <input
          className="input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="2FA code (from your authenticator app)"
          autoFocus
          value={totp}
          onChange={(e) => setTotp(e.target.value)}
        />
      )}
      {error && <p className="error-line">{error}</p>}
      <button type="submit" className="gate-submit" disabled={busy || !email.trim() || !password}>
        {busy ? 'Signing in…' : totpNeeded ? 'Verify code' : 'Sign in'}
      </button>
      <button type="button" className="link-btn" onClick={() => setLegacyMode(true)}>
        Use legacy admin key
      </button>
    </form>
  );
}

function Gate({ onAuthed }: { onAuthed: (s: Session) => void }) {
  const [needed, setNeeded] = useState<boolean | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getBootstrapNeeded().then(
      (r) => alive && setNeeded(r.needed),
      (e: unknown) => {
        // API down or older API without the endpoint — fall through to login.
        if (alive) {
          setNeeded(false);
          setCheckError(e instanceof Error ? e.message : null);
        }
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="gate">
      {needed === null ? (
        <div className="gate-card">
          <h1>
            FLM Admin<span className="dot">.</span>
          </h1>
          <p className="gate-foot">Checking setup…</p>
        </div>
      ) : needed ? (
        <BootstrapForm onAuthed={onAuthed} />
      ) : (
        <>
          <LoginForm onAuthed={onAuthed} />
          {checkError && <p className="error-line">{checkError}</p>}
        </>
      )}
    </div>
  );
}

type BadgeKey = 'reports' | 'verification';
type NavItem = { to: string; label: string; icon: IconName; end?: boolean; badge?: BadgeKey; ownerOnly?: boolean };

/** Sidebar groups — every current admin feature has a home here. */
const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  { label: 'Overview', items: [{ to: '/', label: 'Dashboard', icon: 'grid', end: true }] },
  {
    label: 'People',
    items: [
      { to: '/users', label: 'Users', icon: 'users' },
      { to: '/moderation', label: 'Verification', icon: 'shieldCheck', badge: 'verification' },
      { to: '/importer', label: 'Importer', icon: 'download', ownerOnly: true },
    ],
  },
  { label: 'Trust & Safety', items: [{ to: '/reports', label: 'Reports', icon: 'flag', badge: 'reports' }] },
  {
    label: 'Commerce',
    items: [
      { to: '/payments', label: 'Transactions', icon: 'receipt' },
      { to: '/payment-settings', label: 'Payment settings', icon: 'settings' },
      { to: '/plans', label: 'Plans', icon: 'tag' },
      { to: '/gifts', label: 'Gift shop', icon: 'gift' },
    ],
  },
  { label: 'Community', items: [{ to: '/events', label: 'Events', icon: 'calendar' }] },
  {
    label: 'Engagement',
    items: [
      { to: '/broadcast', label: 'Broadcast', icon: 'megaphone' },
      { to: '/ai', label: 'AI moderation', icon: 'sparkles' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/settings', label: 'Settings', icon: 'settings' },
      { to: '/translations', label: 'Translations', icon: 'languages' },
      { to: '/admins', label: 'Admins', icon: 'shield', ownerOnly: true },
    ],
  },
];

/** Global search (users) in the topbar — type to find, click to open the detail page. */
function TopSearch() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<AdminUser[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      getUsers({ search: term, pageSize: 8 })
        .then((r) => {
          setResults(r.data.slice(0, 8));
          setOpen(true);
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="topbar-search">
      <Icon name="search" size={16} />
      <input
        placeholder="Search users…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      <span className="kbd">⌘K</span>
      {open && results.length > 0 && (
        <div className="search-pop">
          {results.map((u) => (
            <a
              key={u.id}
              onMouseDown={(e) => {
                e.preventDefault();
                navigate(`/users/${u.id}`);
                setOpen(false);
                setQ('');
              }}
            >
              {u.photoUrl ? (
                <img className="avatar sm" src={mediaUrl(u.photoUrl)} alt="" referrerPolicy="no-referrer" />
              ) : (
                <span className="avatar sm">{(u.displayName ?? u.username ?? '?').charAt(0).toUpperCase()}</span>
              )}
              <span>{u.displayName ?? (u.username ? `@${u.username}` : u.id)}</span>
              {u.displayName && u.username && (
                <span className="muted" style={{ marginLeft: 'auto', fontSize: '0.76rem' }}>@{u.username}</span>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/** True on narrow viewports — the sidebar becomes an off-canvas drawer. */
function useIsMobile(query = '(max-width: 880px)') {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setM(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return m;
}

function ShellLayout({
  session,
  onSignOut,
  onIdentity,
}: {
  session: Session;
  onSignOut: () => void;
  onIdentity: (admin: AdminIdentity) => void;
}) {
  const admin = session.kind === 'jwt' ? session.admin : null;
  const role = admin?.role ?? 'owner';
  const { theme, toggle } = useTheme();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [navOpen, setNavOpen] = useState(false); // mobile drawer
  const [show2fa, setShow2fa] = useState(false);
  const [badges, setBadges] = useState<NavBadges>({ reportsOpen: 0, verificationPending: 0 });
  const navigate = useNavigate();

  // Leaving mobile width closes the drawer so the desktop sidebar shows normally.
  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);

  useEffect(() => {
    let alive = true;
    const load = () => getNavBadges().then((b) => alive && setBadges(b)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const badgeFor = (key?: BadgeKey) =>
    key === 'reports' ? badges.reportsOpen : key === 'verification' ? badges.verificationPending : 0;
  const alerts = badges.reportsOpen + badges.verificationPending;

  return (
    <div className={'app-shell' + (collapsed && !isMobile ? ' collapsed' : '') + (navOpen ? ' nav-open' : '')}>
      {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="brand-tile">FLM</span>
          <span className="brand-text">
            <b>FLM</b>
            <span>Admin Console</span>
          </span>
        </div>
        <nav className="sidebar-nav">
          {NAV_GROUPS.map((g) => {
            const items = g.items.filter((it) => !it.ownerOnly || role === 'owner');
            if (!items.length) return null;
            return (
              <div className="nav-group" key={g.label}>
                <div className="nav-group-label">{g.label}</div>
                {items.map((it) => {
                  const n = badgeFor(it.badge);
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.end}
                      title={it.label}
                      onClick={() => setNavOpen(false)}
                      className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
                    >
                      <Icon name={it.icon} size={18} />
                      <span className="nav-label">{it.label}</span>
                      {n > 0 && <span className="nav-badge">{n}</span>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          <span className="avatar sm">{(admin?.name ?? 'A').charAt(0).toUpperCase()}</span>
          <div className="foot-id">
            <b>{admin ? `${admin.name} — ${role}` : 'Legacy key'}</b>
            <span>{admin?.email ?? 'X-Admin-Key session'}</span>
          </div>
          <button className="icon-btn" title="Sign out" onClick={onSignOut}>
            <Icon name="logout" size={16} />
          </button>
        </div>
      </aside>

      <div className="content">
        <header className="topbar">
          <button className="icon-btn" title="Toggle sidebar" onClick={() => (isMobile ? setNavOpen((o) => !o) : setCollapsed((c) => !c))}>
            <Icon name="menu" size={18} />
          </button>
          <TopSearch />
          <div className="topbar-right">
            <span className="pill">{import.meta.env.PROD ? 'Production' : 'Development'}</span>
            <button className="icon-btn" title="Toggle light / dark" onClick={toggle}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} />
            </button>
            <button className="icon-btn" title="Open reports" onClick={() => navigate('/reports')}>
              <Icon name="bell" size={17} />
              {alerts > 0 && (
                <span style={{ position: 'absolute', top: 7, right: 8, width: 7, height: 7, borderRadius: '50%', background: 'var(--pink)' }} />
              )}
            </button>
            {admin && !admin.totpEnrolled && (
              <button className="btn" onClick={() => setShow2fa(true)}>
                Enable 2FA
              </button>
            )}
          </div>
        </header>

        {show2fa && admin && (
          <TwoFactorPanel
            onClose={() => setShow2fa(false)}
            onEnrolled={(next) => {
              onIdentity(next);
              setShow2fa(false);
            }}
          />
        )}

        <main className="main">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="/users" element={<Users />} />
            <Route path="/users/:id" element={<UserDetail />} />
            {role === 'owner' && <Route path="/importer" element={<Importer />} />}
            <Route path="/moderation" element={<Moderation />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/payment-settings" element={<PaymentSettings />} />
            <Route path="/plans" element={<Catalog />} />
            <Route path="/gifts" element={<Catalog />} />
            <Route path="/events" element={<Catalog />} />
            <Route path="/ai" element={<Ai />} />
            <Route path="/broadcast" element={<Broadcast />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/translations" element={<Translations />} />
            {role === 'owner' && <Route path="/admins" element={<Admins />} />}
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function Shell(props: { session: Session; onSignOut: () => void; onIdentity: (admin: AdminIdentity) => void }) {
  return (
    <BrowserRouter>
      <ShellLayout {...props} />
    </BrowserRouter>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null>(initialSession);

  useEffect(() => {
    const onUnauthorized = () => setSession(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  if (!session) {
    return <Gate onAuthed={setSession} />;
  }

  return (
    <Shell
      session={session}
      onIdentity={(admin) => setSession({ kind: 'jwt', admin })}
      onSignOut={() => {
        clearAdminAuth();
        setSession(null);
      }}
    />
  );
}
